/**
 * Timeline State Hook
 *
 * React hook managing timeline state with undo/redo history stack.
 * Handles all timeline editing operations: add, remove, reorder, split, trim,
 * speed control, copy/paste, and type-aware selection.
 */

import { useReducer, useCallback, useMemo } from 'react'
import type {
  TimelineState,
  TimelineClip,
  TimelineAudioClip,
  TimelineAction,
  TimelineHistoryEntry,
} from './types'

interface TimelineStateWithHistory {
  present: TimelineState
  past: TimelineHistoryEntry[]
  future: TimelineHistoryEntry[]
}

const MAX_HISTORY_DEPTH = 50

/** Maximum number of video clips allowed on the timeline */
const MAX_CLIPS = 99

/**
 * Create initial timeline state
 */
function createInitialState(): TimelineState {
  return {
    clips: [],
    audioClips: [],
    playheadPosition: 0,
    zoomLevel: 50, // 50 pixels per second default
    isPlaying: false,
    selectedClipId: null,
    selectedClipType: null,
    totalDuration: 0,
    copiedClip: null,
    copiedClipType: null,
  }
}

/**
 * Compute total duration from clips
 */
function computeTotalDuration(clips: TimelineClip[]): number {
  return clips.reduce((total, clip) => total + clip.effectiveDuration, 0)
}


/**
 * Timeline reducer handling all state mutations
 */
function timelineReducer(
  state: TimelineStateWithHistory,
  action: TimelineAction
): TimelineStateWithHistory {
  const { present, past, future } = state

  // Helper to push current state to history
  const pushHistory = (newState: TimelineState, description: string): TimelineStateWithHistory => {
    const newPast = [
      ...past,
      {
        state: present,
        description,
        timestamp: Date.now(),
      },
    ].slice(-MAX_HISTORY_DEPTH)

    return {
      present: newState,
      past: newPast,
      future: [], // Clear future when new action performed
    }
  }

  switch (action.type) {
    case 'ADD_CLIP': {
      // Enforce 99-clip max
      if (present.clips.length >= MAX_CLIPS) {
        return state
      }
      const newClips = [...present.clips, action.clip].sort((a, b) => a.order - b.order)
      const newState = {
        ...present,
        clips: newClips,
        totalDuration: computeTotalDuration(newClips),
      }
      return pushHistory(newState, `Add clip: ${action.clip.label}`)
    }

    case 'REMOVE_CLIP': {
      const newClips = present.clips.filter((c) => c.id !== action.clipId)
      // Reorder remaining clips
      const reorderedClips = newClips.map((clip, index) => ({ ...clip, order: index }))
      const newState = {
        ...present,
        clips: reorderedClips,
        selectedClipId: present.selectedClipId === action.clipId ? null : present.selectedClipId,
        selectedClipType: present.selectedClipId === action.clipId ? null : present.selectedClipType,
        totalDuration: computeTotalDuration(reorderedClips),
      }
      return pushHistory(newState, `Remove clip: ${action.clipId}`)
    }

    case 'REORDER_CLIP': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const otherClips = present.clips.filter((c) => c.id !== action.clipId)

      // Insert at new position
      const newClips = [
        ...otherClips.slice(0, action.newOrder),
        clip,
        ...otherClips.slice(action.newOrder),
      ]

      // Update order indices
      const reorderedClips = newClips.map((c, index) => ({ ...c, order: index }))

      const newState = {
        ...present,
        clips: reorderedClips,
      }
      return pushHistory(newState, `Reorder clip: ${clip.label} to position ${action.newOrder}`)
    }

    case 'SPLIT_CLIP': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const playheadPosition = action.splitTime

      // Calculate clip's timeline start position
      const clipTimelineStart = present.clips
        .filter((c) => c.order < clip.order)
        .reduce((sum, c) => sum + c.effectiveDuration, 0)

      // Calculate split position relative to clip start
      const relativeTime = playheadPosition - clipTimelineStart

      // Validate split point is within clip bounds
      if (relativeTime <= 0 || relativeTime >= clip.effectiveDuration) {
        // Split point outside valid range
        return state
      }

      // Create two new clips (speed-aware: convert relative time back to source time)
      const sourceRelativeTime = relativeTime * clip.speed

      const firstClip: TimelineClip = {
        ...clip,
        id: `${clip.id}-split-1`,
        trimEnd: clip.trimEnd + (clip.originalDuration - clip.trimStart - clip.trimEnd - sourceRelativeTime),
        effectiveDuration: relativeTime,
        label: `${clip.label} (1)`,
      }

      const secondClip: TimelineClip = {
        ...clip,
        id: `${clip.id}-split-2`,
        trimStart: clip.trimStart + sourceRelativeTime,
        effectiveDuration: clip.effectiveDuration - relativeTime,
        order: clip.order + 1,
        label: `${clip.label} (2)`,
      }

      // Replace original clip with two new clips
      const newClips = [
        ...present.clips.slice(0, clipIndex),
        firstClip,
        secondClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      // Update order indices for clips after the split
      const reorderedClips = newClips.map((c, index) => ({ ...c, order: index }))

      const newState = {
        ...present,
        clips: reorderedClips,
        totalDuration: computeTotalDuration(reorderedClips),
      }
      return pushHistory(newState, `Split clip: ${clip.label} at ${playheadPosition}s`)
    }

    case 'TRIM_CLIP': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      const newClip = {
        ...clip,
        trimStart,
        trimEnd,
        effectiveDuration: (clip.originalDuration - trimStart - trimEnd) / clip.speed,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
        totalDuration: computeTotalDuration(newClips),
      }
      return pushHistory(
        newState,
        `Trim clip: ${clip.label} (start: ${action.trimStart}, end: ${action.trimEnd})`
      )
    }

    case 'TRIM_CLIP_PREVIEW': {
      // Same as TRIM_CLIP but without pushing to history (used during drag)
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      const newClip = {
        ...clip,
        trimStart,
        trimEnd,
        effectiveDuration: (clip.originalDuration - trimStart - trimEnd) / clip.speed,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      return {
        ...state,
        present: {
          ...present,
          clips: newClips,
          totalDuration: computeTotalDuration(newClips),
        },
      }
    }

    case 'UPDATE_CLIP_THUMBNAILS': {
      // Update thumbnails for a clip (doesn't go into history)
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const newClip = {
        ...clip,
        thumbnails: action.thumbnails,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      return {
        ...state,
        present: {
          ...present,
          clips: newClips,
        },
      }
    }

    case 'SELECT_CLIP': {
      // Type-aware selection: set both selectedClipId and selectedClipType
      return {
        ...state,
        present: {
          ...present,
          selectedClipId: action.clipId,
          selectedClipType: action.clipId === null ? null : (action.clipType ?? null),
        },
      }
    }

    case 'SET_PLAYHEAD': {
      // Playhead movement doesn't go into history
      return {
        ...state,
        present: {
          ...present,
          playheadPosition: Math.max(0, Math.min(action.position, present.totalDuration)),
        },
      }
    }

    case 'SET_ZOOM': {
      // Zoom doesn't go into history
      return {
        ...state,
        present: {
          ...present,
          zoomLevel: Math.max(10, Math.min(200, action.zoomLevel)), // 10-200 pixels per second
        },
      }
    }

    case 'PLAY': {
      return {
        ...state,
        present: {
          ...present,
          isPlaying: true,
        },
      }
    }

    case 'PAUSE': {
      return {
        ...state,
        present: {
          ...present,
          isPlaying: false,
        },
      }
    }

    case 'ADD_AUDIO_CLIP': {
      const newAudioClips = [...present.audioClips, action.clip]
      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Add audio clip`)
    }

    case 'REMOVE_AUDIO_CLIP': {
      const newAudioClips = present.audioClips.filter((c) => c.id !== action.clipId)
      const newState = {
        ...present,
        audioClips: newAudioClips,
        selectedClipId: present.selectedClipId === action.clipId ? null : present.selectedClipId,
        selectedClipType: present.selectedClipId === action.clipId ? null : present.selectedClipType,
      }
      return pushHistory(newState, `Remove audio clip`)
    }

    case 'SET_AUDIO_VOLUME': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...newAudioClips[clipIndex],
        volume: Math.max(0, Math.min(1, action.volume)),
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Set audio volume: ${action.volume}`)
    }

    case 'TOGGLE_AUDIO_MUTE': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...newAudioClips[clipIndex],
        muted: !newAudioClips[clipIndex].muted,
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Toggle audio mute`)
    }

    case 'SET_AUDIO_OFFSET': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...newAudioClips[clipIndex],
        startOffset: Math.max(0, action.startOffset),
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Set audio offset: ${action.startOffset}s`)
    }

    case 'TRIM_AUDIO_CLIP': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...clip,
        trimStart,
        trimEnd,
        effectiveDuration: (clip.originalDuration - trimStart - trimEnd) / clip.speed,
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Trim audio clip`)
    }

    case 'SET_CLIP_SPEED': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const speed = Math.max(0.1, action.speed) // Minimum speed 0.1x
      const newClip = {
        ...clip,
        speed,
        effectiveDuration: (clip.originalDuration - clip.trimStart - clip.trimEnd) / speed,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
        totalDuration: computeTotalDuration(newClips),
      }
      return pushHistory(newState, `Set clip speed: ${speed}x`)
    }

    case 'SET_CLIP_VOLUME': {
      // Delegates to audio clip volume — TimelineClip has no volume field,
      // so this targets the audio clip whose ID matches.
      return timelineReducer(state, {
        type: 'SET_AUDIO_VOLUME',
        clipId: action.clipId,
        volume: action.volume,
      })
    }

    case 'SET_AUDIO_SPEED': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const speed = Math.max(0.1, action.speed) // Minimum speed 0.1x
      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...clip,
        speed,
        effectiveDuration: (clip.originalDuration - clip.trimStart - clip.trimEnd) / speed,
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Set audio speed: ${speed}x`)
    }

    case 'SPLIT_AUDIO_CLIP': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const splitTime = action.splitTime

      // Calculate relative split position within the audio clip
      const relativeTime = splitTime - clip.startOffset

      // Validate split point is within clip bounds
      if (relativeTime <= 0 || relativeTime >= clip.effectiveDuration) {
        return state
      }

      // Convert relative time back to source time (speed-aware)
      const sourceRelativeTime = relativeTime * clip.speed

      const firstClip: TimelineAudioClip = {
        ...clip,
        id: `${clip.id}-split-1`,
        trimEnd: clip.trimEnd + (clip.originalDuration - clip.trimStart - clip.trimEnd - sourceRelativeTime),
        effectiveDuration: relativeTime,
      }

      const secondClip: TimelineAudioClip = {
        ...clip,
        id: `${clip.id}-split-2`,
        trimStart: clip.trimStart + sourceRelativeTime,
        effectiveDuration: clip.effectiveDuration - relativeTime,
        startOffset: clip.startOffset + relativeTime,
      }

      const newAudioClips = [
        ...present.audioClips.slice(0, clipIndex),
        firstClip,
        secondClip,
        ...present.audioClips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Split audio clip at ${splitTime}s`)
    }

    case 'REORDER_AUDIO_CLIP': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const otherClips = present.audioClips.filter((c) => c.id !== action.clipId)

      // Insert at new position
      const newAudioClips = [
        ...otherClips.slice(0, action.newOrder),
        clip,
        ...otherClips.slice(action.newOrder),
      ]

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Reorder audio clip to position ${action.newOrder}`)
    }

    case 'COPY_CLIP': {
      // Store selected clip in clipboard without pushing to history
      if (!present.selectedClipId || !present.selectedClipType) {
        return state
      }

      let clipToCopy: TimelineClip | TimelineAudioClip | null = null

      if (present.selectedClipType === 'video') {
        clipToCopy = present.clips.find((c) => c.id === present.selectedClipId) ?? null
      } else {
        clipToCopy = present.audioClips.find((c) => c.id === present.selectedClipId) ?? null
      }

      if (!clipToCopy) return state

      return {
        ...state,
        present: {
          ...present,
          copiedClip: { ...clipToCopy },
          copiedClipType: present.selectedClipType,
        },
      }
    }

    case 'PASTE_CLIP': {
      if (!present.copiedClip || !present.copiedClipType) {
        return state
      }

      if (present.copiedClipType === 'video') {
        // Enforce 99-clip max
        if (present.clips.length >= MAX_CLIPS) {
          return state
        }

        const copiedVideoClip = present.copiedClip as TimelineClip
        const newClip: TimelineClip = {
          ...copiedVideoClip,
          id: crypto.randomUUID(),
          order: present.clips.length,
          label: `${copiedVideoClip.label} (copy)`,
        }

        const newClips = [...present.clips, newClip].sort((a, b) => a.order - b.order)
        const newState = {
          ...present,
          clips: newClips,
          totalDuration: computeTotalDuration(newClips),
        }
        return pushHistory(newState, `Paste clip: ${newClip.label}`)
      } else {
        const copiedAudioClip = present.copiedClip as TimelineAudioClip
        const newClip: TimelineAudioClip = {
          ...copiedAudioClip,
          id: crypto.randomUUID(),
          startOffset: present.playheadPosition,
        }

        const newAudioClips = [...present.audioClips, newClip]
        const newState = {
          ...present,
          audioClips: newAudioClips,
        }
        return pushHistory(newState, `Paste audio clip`)
      }
    }

    case 'DELETE_SELECTED_CLIP': {
      if (!present.selectedClipId || !present.selectedClipType) {
        return state
      }

      if (present.selectedClipType === 'video') {
        const newClips = present.clips.filter((c) => c.id !== present.selectedClipId)
        const reorderedClips = newClips.map((clip, index) => ({ ...clip, order: index }))
        const newState = {
          ...present,
          clips: reorderedClips,
          selectedClipId: null,
          selectedClipType: null,
          totalDuration: computeTotalDuration(reorderedClips),
        }
        return pushHistory(newState, `Delete selected video clip`)
      } else {
        const newAudioClips = present.audioClips.filter((c) => c.id !== present.selectedClipId)
        const newState = {
          ...present,
          audioClips: newAudioClips,
          selectedClipId: null,
          selectedClipType: null,
        }
        return pushHistory(newState, `Delete selected audio clip`)
      }
    }

    case 'SPLIT_SELECTED_CLIP': {
      if (!present.selectedClipId || !present.selectedClipType) {
        return state
      }

      if (present.selectedClipType === 'video') {
        // Delegate to SPLIT_CLIP logic
        return timelineReducer(state, {
          type: 'SPLIT_CLIP',
          clipId: present.selectedClipId,
          splitTime: present.playheadPosition,
        })
      } else {
        // Delegate to SPLIT_AUDIO_CLIP logic
        return timelineReducer(state, {
          type: 'SPLIT_AUDIO_CLIP',
          clipId: present.selectedClipId,
          splitTime: present.playheadPosition,
        })
      }
    }

    case 'UNDO': {
      if (past.length === 0) return state

      const previous = past[past.length - 1]
      const newPast = past.slice(0, -1)
      const newFuture = [
        {
          state: present,
          description: 'undo',
          timestamp: Date.now(),
        },
        ...future,
      ]

      return {
        present: previous.state,
        past: newPast,
        future: newFuture,
      }
    }

    case 'REDO': {
      if (future.length === 0) return state

      const next = future[0]
      const newFuture = future.slice(1)
      const newPast = [
        ...past,
        {
          state: present,
          description: 'redo',
          timestamp: Date.now(),
        },
      ]

      return {
        present: next.state,
        past: newPast,
        future: newFuture,
      }
    }

    case 'RESTORE_STATE': {
      // Used for loading from Yjs or initial state
      return {
        present: action.state,
        past: [],
        future: [],
      }
    }

    default:
      return state
  }
}

/**
 * Hook for managing timeline state with undo/redo
 */
export function useTimelineState(initialState?: TimelineState) {
  const [state, dispatch] = useReducer(timelineReducer, {
    present: initialState || createInitialState(),
    past: [],
    future: [],
  })

  // Action creators
  const addClip = useCallback((clip: TimelineClip) => {
    dispatch({ type: 'ADD_CLIP', clip })
  }, [])

  const removeClip = useCallback((clipId: string) => {
    dispatch({ type: 'REMOVE_CLIP', clipId })
  }, [])

  const reorderClip = useCallback((clipId: string, newOrder: number) => {
    dispatch({ type: 'REORDER_CLIP', clipId, newOrder })
  }, [])

  const splitClip = useCallback((clipId: string, splitTime: number) => {
    dispatch({ type: 'SPLIT_CLIP', clipId, splitTime })
  }, [])

  const trimClip = useCallback((clipId: string, trimStart: number, trimEnd: number) => {
    dispatch({ type: 'TRIM_CLIP', clipId, trimStart, trimEnd })
  }, [])

  const trimClipPreview = useCallback((clipId: string, trimStart: number, trimEnd: number) => {
    dispatch({ type: 'TRIM_CLIP_PREVIEW', clipId, trimStart, trimEnd })
  }, [])

  const updateClipThumbnails = useCallback((clipId: string, thumbnails: string[]) => {
    dispatch({ type: 'UPDATE_CLIP_THUMBNAILS', clipId, thumbnails })
  }, [])

  const selectClip = useCallback((clipId: string | null, clipType?: 'video' | 'audio' | null) => {
    dispatch({ type: 'SELECT_CLIP', clipId, clipType })
  }, [])

  const setPlayhead = useCallback((position: number) => {
    dispatch({ type: 'SET_PLAYHEAD', position })
  }, [])

  const setZoom = useCallback((zoomLevel: number) => {
    dispatch({ type: 'SET_ZOOM', zoomLevel })
  }, [])

  const play = useCallback(() => {
    dispatch({ type: 'PLAY' })
  }, [])

  const pause = useCallback(() => {
    dispatch({ type: 'PAUSE' })
  }, [])

  const togglePlayPause = useCallback(() => {
    dispatch({ type: state.present.isPlaying ? 'PAUSE' : 'PLAY' })
  }, [state.present.isPlaying])

  const addAudioClip = useCallback((clip: TimelineAudioClip) => {
    dispatch({ type: 'ADD_AUDIO_CLIP', clip })
  }, [])

  const removeAudioClip = useCallback((clipId: string) => {
    dispatch({ type: 'REMOVE_AUDIO_CLIP', clipId })
  }, [])

  const setAudioVolume = useCallback((clipId: string, volume: number) => {
    dispatch({ type: 'SET_AUDIO_VOLUME', clipId, volume })
  }, [])

  const toggleAudioMute = useCallback((clipId: string) => {
    dispatch({ type: 'TOGGLE_AUDIO_MUTE', clipId })
  }, [])

  const setAudioOffset = useCallback((clipId: string, startOffset: number) => {
    dispatch({ type: 'SET_AUDIO_OFFSET', clipId, startOffset })
  }, [])

  const trimAudioClip = useCallback((clipId: string, trimStart: number, trimEnd: number) => {
    dispatch({ type: 'TRIM_AUDIO_CLIP', clipId, trimStart, trimEnd })
  }, [])

  const setClipSpeed = useCallback((clipId: string, speed: number) => {
    dispatch({ type: 'SET_CLIP_SPEED', clipId, speed })
  }, [])

  const setClipVolume = useCallback((clipId: string, volume: number) => {
    dispatch({ type: 'SET_CLIP_VOLUME', clipId, volume })
  }, [])

  const setAudioSpeed = useCallback((clipId: string, speed: number) => {
    dispatch({ type: 'SET_AUDIO_SPEED', clipId, speed })
  }, [])

  const splitAudioClip = useCallback((clipId: string, splitTime: number) => {
    dispatch({ type: 'SPLIT_AUDIO_CLIP', clipId, splitTime })
  }, [])

  const reorderAudioClip = useCallback((clipId: string, newOrder: number) => {
    dispatch({ type: 'REORDER_AUDIO_CLIP', clipId, newOrder })
  }, [])

  const copyClip = useCallback(() => {
    dispatch({ type: 'COPY_CLIP' })
  }, [])

  const pasteClip = useCallback(() => {
    dispatch({ type: 'PASTE_CLIP' })
  }, [])

  const deleteSelectedClip = useCallback(() => {
    dispatch({ type: 'DELETE_SELECTED_CLIP' })
  }, [])

  const splitSelectedClip = useCallback(() => {
    dispatch({ type: 'SPLIT_SELECTED_CLIP' })
  }, [])

  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' })
  }, [])

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' })
  }, [])

  const restoreState = useCallback((newState: TimelineState) => {
    dispatch({ type: 'RESTORE_STATE', state: newState })
  }, [])

  // Derived state
  const canUndo = state.past.length > 0
  const canRedo = state.future.length > 0

  return useMemo(
    () => ({
      // Current state
      timelineState: state.present,

      // Actions
      addClip,
      removeClip,
      reorderClip,
      splitClip,
      trimClip,
      trimClipPreview,
      updateClipThumbnails,
      selectClip,
      setPlayhead,
      setZoom,
      play,
      pause,
      togglePlayPause,
      addAudioClip,
      removeAudioClip,
      setAudioVolume,
      toggleAudioMute,
      setAudioOffset,
      trimAudioClip,
      setClipSpeed,
      setClipVolume,
      setAudioSpeed,
      splitAudioClip,
      reorderAudioClip,
      copyClip,
      pasteClip,
      deleteSelectedClip,
      splitSelectedClip,
      undo,
      redo,
      restoreState,

      // Undo/Redo state
      canUndo,
      canRedo,
    }),
    [
      state.present,
      addClip,
      removeClip,
      reorderClip,
      splitClip,
      trimClip,
      trimClipPreview,
      updateClipThumbnails,
      selectClip,
      setPlayhead,
      setZoom,
      play,
      pause,
      togglePlayPause,
      addAudioClip,
      removeAudioClip,
      setAudioVolume,
      toggleAudioMute,
      setAudioOffset,
      trimAudioClip,
      setClipSpeed,
      setClipVolume,
      setAudioSpeed,
      splitAudioClip,
      reorderAudioClip,
      copyClip,
      pasteClip,
      deleteSelectedClip,
      splitSelectedClip,
      undo,
      redo,
      restoreState,
      canUndo,
      canRedo,
    ]
  )
}
